import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, CircularProgress, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DriveFile } from '../services/myDriveService';
import axios from '../../../../api/axios';

interface Props {
  file: DriveFile | null;
  onClose: () => void;
}

function isImage(mime: string)  { return mime.startsWith('image/'); }
function isPdf(mime: string)    { return mime === 'application/pdf'; }
function isOffice(mime: string) {
  return [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/msword', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint',
  ].includes(mime);
}

function canPreview(mime: string) {
  return isImage(mime) || isPdf(mime) || isOffice(mime);
}

export default function FilePreviewModal({ file, onClose }: Props) {
  const [blobUrl, setBlobUrl]   = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error,   setError]     = useState<string | null>(null);

  useEffect(() => {
    if (!file) { setBlobUrl(null); setError(null); return; }
    if (!canPreview(file.mime_type)) return;

    let url: string;
    setLoading(true);
    setError(null);
    setBlobUrl(null);

    axios.get(`/api/ee/my-drive/files/${file.id}/preview`, { responseType: 'blob' })
      .then(r => {
        url = URL.createObjectURL(r.data);
        setBlobUrl(url);
      })
      .catch(() => setError('預覽載入失敗，請稍後再試。'))
      .finally(() => setLoading(false));

    return () => { if (url) URL.revokeObjectURL(url); };
  }, [file]);

  if (!file) return null;

  return (
    <Dialog open maxWidth="lg" fullWidth onClose={onClose}>
      <DialogTitle sx={{ pr: 6 }}>
        {file.filename}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading && <CircularProgress />}
        {error && <Typography color="error">{error}</Typography>}
        {!loading && !error && blobUrl && isImage(file.mime_type) && (
          <Box component="img" src={blobUrl} alt={file.filename}
               sx={{ maxWidth: '100%', maxHeight: '100%', display: 'block', mx: 'auto' }} />
        )}
        {!loading && !error && blobUrl && (isPdf(file.mime_type) || isOffice(file.mime_type)) && (
          <Box component="iframe" src={blobUrl} width="100%" height="100%" sx={{ border: 0 }} />
        )}
        {!loading && !error && !canPreview(file.mime_type) && (
          <Typography color="text.secondary">
            此檔案類型不支援預覽（{file.mime_type}）
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
